import type { Router } from '@aikoa/api'
import { useMutation } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import { getDocumentAsync } from 'expo-document-picker'
import { EncodingType, readAsStringAsync } from 'expo-file-system/legacy'
import { useCallback, useState } from 'react'
import { Platform, ScrollView, View } from 'react-native'
import { useAlert } from '~/components/GlobalAlert'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { Body, Label } from '~/components/typography/Content'
import { Heading2, Heading3 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import { trpc } from '~/lib/trpc'

type AppRouterOutputs = inferRouterOutputs<Router>
type RealtorImportResult = AppRouterOutputs['admin']['importRealtorsCsv']
type InvalidRow = RealtorImportResult['invalidRows'][number]
type FailedRow = RealtorImportResult['failedRows'][number]

function getInvalidRowReasonLabel(reason: InvalidRow['reason']) {
  switch (reason) {
    case 'MISSING_NAME': {
      return 'Missing name'
    }
    case 'MISSING_EMAIL': {
      return 'Missing email'
    }
    case 'INVALID_LANGUAGE': {
      return 'Invalid language'
    }
    default: {
      return 'Unknown error'
    }
  }
}

async function readCsvContent(fileUri: string): Promise<string> {
  if (Platform.OS === 'web') {
    const response = await fetch(fileUri)
    return await response.text()
  }

  return await readAsStringAsync(fileUri, {
    encoding: EncodingType.UTF8,
  })
}

export default function BulkRealtorImportPage() {
  const { showAlert } = useAlert()
  const [selectedFileName, setSelectedFileName] = useState('')
  const [csvContent, setCsvContent] = useState('')
  const [importResult, setImportResult] = useState<RealtorImportResult | null>(null)

  const importMutation = useMutation(
    trpc.admin.importRealtorsCsv.mutationOptions({
      onSuccess: (result) => {
        setImportResult(result)
      },
      onError: () => {
        showAlert({
          type: 'error',
          title: 'Import failed',
          description: 'An error occurred while importing realtors. Please try again.',
        })
      },
    })
  )

  const handlePickCsvFile = useCallback(async () => {
    try {
      const pickerResult = await getDocumentAsync({
        type: ['text/csv', 'text/comma-separated-values', 'application/vnd.ms-excel'],
        copyToCacheDirectory: true,
        multiple: false,
      })

      if (pickerResult.canceled) {
        return
      }

      const selectedFile = pickerResult.assets[0]
      const selectedFileContent = await readCsvContent(selectedFile.uri)

      if (!selectedFileContent.trim()) {
        showAlert({
          type: 'error',
          title: 'Import failed',
          description: 'The selected file is empty.',
        })
        return
      }

      setCsvContent(selectedFileContent)
      setSelectedFileName(selectedFile.name || 'Unnamed file')
      setImportResult(null)
    } catch {
      showAlert({
        type: 'error',
        title: 'Import failed',
        description: 'Failed to read the selected file.',
      })
    }
  }, [showAlert])

  const handleRunImport = useCallback(async () => {
    if (!csvContent) {
      return
    }

    await importMutation.mutateAsync({ csvContent })
  }, [csvContent, importMutation])

  const canSubmit = !!csvContent && !importMutation.isPending

  return (
    <ScreenView>
      <ScrollView>
        <ContentBox className="gap-6" variant="transparent">
          <View className="gap-3">
            <Heading2 className="text-center">Bulk Realtor Import</Heading2>
            <Body className="text-center text-secondary">
              Import multiple realtors at once using a CSV file
            </Body>
          </View>

          <View className="gap-3">
            <Heading3>Select CSV File</Heading3>
            <Button variant="outline" onPress={() => void handlePickCsvFile()}>
              <ButtonLabel>Pick CSV File</ButtonLabel>
            </Button>
            {!!selectedFileName && (
              <Label className="text-sm text-secondary">Selected file: {selectedFileName}</Label>
            )}
            <Body className="text-xs text-secondary">
              CSV format: name, email, language (optional)
            </Body>
          </View>

          <Button disabled={!canSubmit} onPress={() => void handleRunImport()}>
            <ButtonLabel>{importMutation.isPending ? 'Importing...' : 'Run Import'}</ButtonLabel>
          </Button>

          {!!importResult && (
            <View className="gap-4">
              <Heading3>Import Results</Heading3>
              <View className="gap-2 rounded-lg border border-neutral-200 bg-white p-4">
                <Label className="text-sm text-secondary">
                  Users created: {importResult.usersCreated}
                </Label>
                <Label className="text-sm text-secondary">
                  Users failed: {importResult.usersFailed}
                </Label>
              </View>

              {importResult.failedRows.length > 0 && (
                <View className="gap-2">
                  <Heading3>Failed Rows</Heading3>
                  {importResult.failedRows.map((failedRow: FailedRow) => (
                    <View
                      key={`failed-${failedRow.rowNumber}`}
                      className="gap-1 rounded-lg border border-red-300 bg-red-50 p-3">
                      <Text className="font-medium text-sm">
                        Row {failedRow.rowNumber} — {failedRow.email}
                      </Text>
                      <Label className="text-xs text-secondary">{failedRow.error}</Label>
                    </View>
                  ))}
                </View>
              )}

              <View className="gap-2">
                <Heading3>Invalid Rows</Heading3>
                {importResult.invalidRows.length === 0 ? (
                  <Body className="text-sm text-secondary">No invalid rows</Body>
                ) : (
                  <View className="gap-2">
                    {importResult.invalidRows.map((invalidRow: InvalidRow) => (
                      <View
                        key={`${invalidRow.rowNumber}-${invalidRow.reason}`}
                        className="gap-1 rounded-lg border border-amber-300 bg-amber-50 p-3">
                        <Text className="font-medium text-sm">Row {invalidRow.rowNumber}</Text>
                        <Label className="text-xs text-secondary">
                          {getInvalidRowReasonLabel(invalidRow.reason)}
                        </Label>
                        {!!invalidRow.email && (
                          <Label className="text-xs text-secondary">{invalidRow.email}</Label>
                        )}
                      </View>
                    ))}
                  </View>
                )}
              </View>
            </View>
          )}
        </ContentBox>
      </ScrollView>
    </ScreenView>
  )
}
